﻿import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Table } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { FETCH_ESTATES_SUCCESS, FINAL_ESTATES_REQUEST, INITIAL_ESTATES_REQUEST } from "@/redux/types/maintainers/estates.type";
import { UPDATE_ESTATES_PARENTS_ESTATES } from "@/redux/types/auth.types";
import { FETCHING_ADD_DOWNLOAD } from "@/redux/types/main.types";

import classes from "../maintainers.module.scss";

import api from "@/services/api.service";
import { getToday, hasPermission, toast } from "@/services/Utils";

import Head from "@/components/Content/Head";
import BasePagination from "@/components/Pagination/BasePagination";
import Fetching from "@/components/Fetching/Fetching";
import BasicConfirm from "@/components/Confirm/BasicConfirm";
import EstatesTableHead from "./EstatesTableHead";
import EstatesTableBody from "./EstatesTableBody";
import EstatesForm from "./EstatesForm/EstatesForm";
import EstateOut from "./EstatesForm/EstateOut";

const Estates = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { addToast } = useToasts();
	const navigate = useNavigate();

	const { search, estate_id } = useSelector((state) => state.auth.filter);
	const {
		isFetching,
		quantity,
		last_page,
		from,
		to,
		customers,
		estates_parents,
		categories: allCategories,
	} = useSelector((state) => state.maintainers_estates);
	const { customer } = useSelector((state) => state.auth.user);

	const [selected, setSelected] = useState("");
	const [paginate, setPaginate] = useState(20);
	const [page, setPage] = useState(1);
	const [order, setOrder] = useState("name");
	const [orderType, setOrderType] = useState("asc");
	const [loaded, setLoaded] = useState(false);
	const [permissions, setPermissions] = useState([]);

	const [showDelete, setShowDelete] = useState(false);
	const [showOut, setShowOut] = useState(false);
	const [categories, setCategories] = useState([]);

	useEffect(() => {
		dispatch({ type: INITIAL_ESTATES_REQUEST });
		api.get("/admin/estates", {
			page,
			paginate,
			order,
			orderType,
			search,
			estate_id,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			updateData(response);
		});
		// eslint-disable-next-line
	}, [estate_id, search, order, orderType, paginate, page]);

	useEffect(() => {
		if (customer && customer.category_id) {
			const categoriesID = customer.category_id.split(",").map((id) => parseInt(id));
			setCategories(allCategories.filter((category) => categoriesID.includes(category.value)));
		}
	}, [customer, allCategories]);

	const updateData = (response) => {
		const { estates, customers, permissions: allPermission, estates_parents, categories } = response;
		dispatch({
			type: FETCH_ESTATES_SUCCESS,
			payload: {
				estates: estates.data,
				last_page: estates.last_page,
				from: estates.from,
				to: estates.to,
				quantity: estates.total,
				customers: customers,
				estates_parents: estates_parents,
				categories: categories,
			},
		});
		setPermissions(allPermission);
		setLoaded(true);
	};

	const handleDelete = (item) => {
		setSelected(item.id);
		setShowDelete(true);
	};

	const handleOut = (item) => {
		setSelected(item.id);
		setShowOut(true);
	};

	const confirmDelete = () => {
		setShowDelete(false);
		setPage(1);

		dispatch({ type: INITIAL_ESTATES_REQUEST });
		api.post("/admin/estates/delete", {
			page,
			selected,
			paginate,
			order,
			orderType,
			search,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			setSelected("");
			if (Object.prototype.hasOwnProperty.call(response, "estates")) {
				toast(addToast, `Bodega eliminada correctamente`, "success");
				updateData(response);
			} else {
				dispatch({ type: FINAL_ESTATES_REQUEST });
			}
		});
	};

	const confirmSave = (form, modalFetching, afterSave) => {
		modalFetching(true);
		setPage(1);

		api.post("/admin/estates/save", {
			...form,
			page,
			paginate,
			order,
			orderType,
			search,
			type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
		}).then((response) => {
			const { estates, estates_parents } = response ? response.user : {};
			if (estates) {
				dispatch({
					type: UPDATE_ESTATES_PARENTS_ESTATES,
					payload: {
						estates_parents: estates_parents,
						estates: estates,
					},
				});
			}

			updateData(response);
			modalFetching(false);
			afterSave();
		});
	};

	const exportExcel = () => {
		const filename = `Campos ${getToday()}.xlsx`;

		dispatch({
			type: FETCHING_ADD_DOWNLOAD,
			payload: {
				file: {
					key: Array.from({ length: 25 }, () => Math.random().toString(36)[2] || "0").join(""),
					params: {
						page,
						paginate,
						order,
						orderType,
						search,
						estate_id,
						type: location.pathname.includes("configuracion") ? "settings" : "maintainers",
					},
					show: true,
					url: "/admin/estates/export_excel",
					name: filename,
					type: "excel",
					inExecution: false,
					inProgress: false,
					inChecked: false,
				},
			},
		});
	};

	if (isFetching) {
		return <Fetching />;
	}

	return (
		<>
			<Head
				title={"Bodega"}
				button={hasPermission("create", permissions) ? true : false}
				button_text={`Crear Bodega`}
				action={() => navigate("/configuracion/campos/crear")}
				back_url={"/configuracion?section=general"}
			/>

			{hasPermission("show", permissions) && (
				<Row className={` ${classes.main_row}`}>
					<Col md={12} className={classes.main_col}>
						<Row className={classes.header}>
							<Col md={10}></Col>

							<Col md={2} className={classes.colOptionButton}>
								<div className={`${classes.optionButton} ${classes.exportInventory}`} onClick={() => exportExcel()}>
									<span>Excel</span>
								</div>
							</Col>
						</Row>

						<Row className={classes.table}>
							<div className={classes.table_div}>
								<Table className={classes.table_desktop}>
									<EstatesTableHead order={order} setOrder={setOrder} orderType={orderType} setOrderType={setOrderType} />
									<EstatesTableBody handleDelete={handleDelete} handleOut={handleOut} permissions={permissions} />
								</Table>
							</div>
						</Row>

						<BasePagination
							page={page}
							paginate={paginate}
							last_page={last_page}
							quantity={quantity}
							from={from}
							to={to}
							setPage={setPage}
							setPaginate={setPaginate}
						/>
					</Col>
				</Row>
			)}

			{loaded && (
				<>
					<BasicConfirm
						showConfirm={showDelete}
						setShowConfirm={setShowDelete}
						handleAction={confirmDelete}
						subMessage={`Al eliminar el registro de Bodega se eliminarán todos los datos asociados. ¿Está seguro que desea eliminar?`}
						showValidation={true}
						textValidation={`eliminar bodega y todos sus datos`}
					/>
					<EstatesForm
						customers={customers}
						estates_parents={estates_parents}
						confirmSave={confirmSave}
						permissions={permissions}
						categories={categories}
					/>

					<EstateOut show={showOut} setShow={setShowOut} estate_id={selected} setEstate={setSelected} />
				</>
			)}
		</>
	);
};

export default Estates;
